import { useRef } from 'react';
import { useNavigate } from 'react-router';
import { motion } from 'motion/react';
import { Badge, Button, Card } from '@monksflow/monks-ui';
import { ArrowUpRight } from 'lucide-react';
import { ToolBadge } from '@/shared/ui/ToolBadge';
import { eases } from '@/shared/constants/motion.constants';
import type { FeaturedCardProps } from './FeaturedCard.types';
import { useTranslation } from 'react-i18next';

const isSafari =
  typeof navigator !== 'undefined' && /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

export const FeaturedCard = ({
  id,
  title,
  description,
  poster,
  video,
  category,
  badgeIcon,
  linkTo,
  onClick,
  ctaLabel,
  index,
}: FeaturedCardProps) => {
  const { t } = useTranslation('tools');

  const navigate = useNavigate();

  const handleActivate = () => {
    if (onClick) {
      onClick();
      return;
    }
    if (!linkTo) return;
    void navigate(linkTo);
  };
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const playPromiseRef = useRef<Promise<void> | null>(null);

  const handleMouseEnter = () => {
    if (!videoRef.current) return;
    // `play()` predates its own promise: an environment without media support
    // returns undefined, and reading `.catch` off that throws.
    const playPromise = videoRef.current.play() as Promise<void> | undefined;
    playPromiseRef.current = playPromise ?? null;
    playPromise?.catch(() => {});
  };

  const handleMouseLeave = () => {
    if (videoRef.current) {
      const playPromise = playPromiseRef.current;
      if (playPromise) {
        playPromise
          .then(() => {
            videoRef.current?.pause();
          })
          .catch(() => {});
      } else {
        videoRef.current.pause();
      }
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      className="group relative flex flex-col items-stretch justify-end overflow-hidden rounded-lg p-3 cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      onClick={handleActivate}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleActivate();
        }
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <motion.video
        ref={videoRef}
        className="absolute inset-0 block w-full h-full object-cover pointer-events-none rounded-lg"
        initial={{ scale: 1.6 }}
        animate={{ scale: 1 }}
        transition={{ duration: 1.8, delay: index * 0.15, ease: eases.quartOut }}
        src={video}
        poster={poster}
        muted
        loop
        playsInline
        preload={isSafari ? 'auto' : 'none'}
      />

      {/* Spacer to give the card height above the footer */}
      <div className="relative h-[170px] w-full" />

      {/* Info bar overlaying the bottom */}
      <Card
        variant="default"
        className="relative flex-row items-center gap-4 rounded-lg p-4 shadow-none"
      >
        {category && <ToolBadge toolId={id} category={category} icon={badgeIcon} size="xl" />}
        <div className="flex flex-1 flex-col gap-1 min-w-0">
          <span className="text-xl font-medium tracking-tight text-foreground">{title}</span>
          <span className="text-sm text-foreground-alt">{description}</span>
        </div>
        {/* The badge and the call to action share one slot: the badge says what
            the card is until the pointer arrives, then steps aside for what
            activating it will do. */}
        <span className="relative flex shrink-0 items-center">
          <Badge
            variant="secondary"
            className={
              ctaLabel
                ? 'transition-opacity group-hover:opacity-0 group-focus-within:opacity-0'
                : ''
            }
          >
            {t('toolsHub.featuredCard.featured')}
          </Badge>
          {ctaLabel && (
            <Button
              variant="ghost"
              tabIndex={-1}
              onClick={(event) => {
                // The card behind is the real target; without this the same
                // activation would run twice.
                event.stopPropagation();
                handleActivate();
              }}
              className="absolute top-1/2 right-0 -translate-y-1/2 bg-card opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100"
            >
              {ctaLabel}
              <ArrowUpRight className="size-4" />
            </Button>
          )}
        </span>
      </Card>
    </div>
  );
};
